import { omit } from 'ramda'

import { Palette, range6m1y5y20yAll } from '../'
import {
  CompassAPIDataBase,
  CompassAPIDataPoint,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
} from '../utils/compass'
import { datetimeXAxis } from '../utils/constants'
import type { BaseContext } from './'
import { CommonChartConfig, commonChartOptions } from './common'

interface SeriesData {
  name: string
  data: CompassAPIDataPoint[]
}

interface MeanPoint {
  start_date: string
  end_date: string
  mean: number
}

export interface CompassAPIMultiSeriesWithTwoMeansChartData
  extends CompassAPIDataBase {
  title: string
  data: SeriesData[]
  mean_data: MeanPoint[]
}

export namespace ValuationSpx {
  export namespace Spx {
    const CONTEXT_ID = 'equity:valuation_spx:spx'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      title: string
      chart:
        | 'forward_pe'
        | 'forward_pe_ex_faang_and_it'
        | 'pe_ratio_spy_vs_spy_ex_it_and_faang'
        | 'spy_weights'
        | 'operating_margin'
        | 'free_cash_flow_per_earnings'
        | 'free_cash_flow_per_sales'
        | 'price_per_free_cash_flow'
        | 'free_cash_flow_vs_return_of_capital'
        | 'payout'
        | 'forward_earnings_yield_bond_yield'
        | 'bond_yield'
        | 'roe'
        | 'roa'
        | 's_and_p_performances_3y'
        | 's_and_p_performances_itd'

      context: typeof CONTEXT_ID
    }

    export type ChartData =
      | CompassAPIMultiSeriesChartData
      | CompassAPIMultiSeriesWithTwoMeansChartData

    const isPercent = (chart: ChartConfig['chart']) =>
      chart === 'forward_earnings_yield_bond_yield' ||
      chart === 'bond_yield' ||
      chart === 's_and_p_performances_3y' ||
      chart === 's_and_p_performances_itd'

    const hasMsdIndicators = (chart: ChartConfig['chart']) =>
      chart === 'forward_pe' ||
      chart === 'forward_pe_ex_faang_and_it' ||
      chart === 'price_per_free_cash_flow' ||
      chart === 'forward_earnings_yield_bond_yield' ||
      chart === 'bond_yield'

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'forward_pe':
          case 'forward_pe_ex_faang_and_it':
          case 'price_per_free_cash_flow':
          case 'forward_earnings_yield_bond_yield':
          case 'bond_yield':
          case 's_and_p_performances_3y':
          case 's_and_p_performances_itd':
            return getMultipleLineChartProps(
              Context,
              {
                addMinMaxIndicators: true,
                chartTitle: data.title ?? config.title,
                ...(isPercent(config.chart)
                  ? { convertToPercentages: Array(data.data.length).fill(true) }
                  : {}),
                ...(hasMsdIndicators(config.chart)
                  ? { msdIndicators: { avg: true, sd1: true, sd2: true } }
                  : {}),
                rangeSelector: range6m1y5y20yAll,
              },
              {
                boostThreshold: Array(data.data.length).fill(10000),
                name: [
                  config.chart === 'forward_pe'
                    ? 'Forward PE'
                    : config.chart === 'price_per_free_cash_flow'
                    ? 'Price / Free cash flow'
                    : config.chart === 'forward_earnings_yield_bond_yield'
                    ? 'Forward earnings yield'
                    : config.chart === 'bond_yield'
                    ? 'Bond yield'
                    : '',
                ],
              }
            )(config, data as CompassAPIMultiSeriesChartData)
          case 'pe_ratio_spy_vs_spy_ex_it_and_faang':
            return getRatioChartProps(config, data)
          case 'spy_weights':
            return getMultipleColumnChartProps(
              Context,
              {
                chartTitle: 'S&P Sector weights',
                convertToPercentages: Array(data.data.length).fill(true),
                plotOptions: { area: { stacking: 'normal' } },
                tooltip: { xDateFormat: '%Y' },
                xAxis: { labels: { format: '{value:%Y}' } },
              },
              { type: Array(data.data.length).fill('area') }
            )(config, data as CompassAPIMultiSeriesChartData)
          case 'operating_margin':
            return getMarginChartProps(
              config,
              data as CompassAPIMultiSeriesWithTwoMeansChartData
            )
          case 'free_cash_flow_per_earnings':
          case 'free_cash_flow_per_sales':
          case 'payout':
            return getFreeCashFlowChartProps(
              config,
              data as CompassAPIMultiSeriesWithTwoMeansChartData
            )
          case 'free_cash_flow_vs_return_of_capital':
            return getMultipleLineChartProps(
              Context,
              {
                addMinMaxIndicators: true,
                rangeSelector: omit(['selected'], range6m1y5y20yAll),
              },
              {
                boostThreshold: Array(data.data.length).fill(10000),
                name: ['FCF', 'Return of capital'],
              }
            )(config, data as CompassAPIMultiSeriesChartData)
          case 'roe':
          case 'roa':
            return getMultipleLineChartPropsWrapper(config, data)
        }
      },
    }

    const getRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: config.title,
          msdIndicatorsSeriesIndexes: [0],
          msdIndicators: { avg: true, sd1: true, sd2: true },
          yAxis: [
            { height: '60%', id: 'primary', title: { text: '' } },
            { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
          ],
        },
        {
          name: ['Ratio', 'SPY', 'SPY - Tech && FAANG'],
          yAxis: ['primary', 'secondary', 'secondary'],
        }
      )(config, data)

    const getMeansSeries = (options: {
      color: string
      series: SeriesData
      meanPoints: MeanPoint[]
      id: string
    }) => {
      const { series, meanPoints, color, id } = options

      const getIdx = (seriesData: CompassAPIDataPoint[], searchDate: string) =>
        seriesData.findIndex((el) => el.date === searchDate)

      const getMeanData = (config: {
        seriesData: CompassAPIDataPoint[]
        mean: number
        firstIdx: number
        lastIdx: number
      }) => {
        const { seriesData, firstIdx, lastIdx, mean } = config
        return seriesData.slice(firstIdx, lastIdx).map((el, idx) => ({
          date: el.date,
          value: mean,
          label: idx === lastIdx - firstIdx - 1 ? mean : '',
        }))
      }

      const getMeans = (options: {
        series: any[]
        meanId: 'first' | 'second'
        color: string
        referenceId: string
      }) => {
        const { series, meanId, color, referenceId } = options
        return {
          color,
          data: [0, series.length - 1].map((idx) => {
            const { date, value, label } = series[idx] ?? {}
            return { x: new Date(date).getTime(), y: 100 * value, label }
          }),
          dashStyle: 'LongDash',
          dataLabels: { enabled: true, formatter: dataLabelsFormatter },
          id: `${referenceId
            .toLowerCase()
            .replace(' ', '-')}-${meanId}-half-mean`,
          name: `${referenceId}: ${
            meanId === 'first' ? 'First' : 'Second'
          } half mean`,
          showInLegend: false,
          type: 'line',
          yAxis: 'default',
        }
      }

      return [
        getMeans({
          series: getMeanData({
            seriesData: series.data,
            firstIdx: getIdx(series.data, meanPoints[0].start_date),
            lastIdx: getIdx(series.data, meanPoints[0].end_date),
            mean: meanPoints[0].mean,
          }),
          meanId: 'first',
          referenceId: id,
          color,
        }),
        getMeans({
          series: getMeanData({
            seriesData: series.data,
            firstIdx: getIdx(series.data, meanPoints[1].start_date),
            lastIdx: getIdx(series.data, meanPoints[1].end_date),
            mean: meanPoints[1].mean,
          }),
          meanId: 'second',
          referenceId: id,
          color,
        }),
      ]
    }

    const PERCENTUAL_Y_AXIS = {
      id: 'default',
      labels: { format: '{value}%' },
      opposite: false,
      title: { text: '' },
    }

    const PERCENTUAL_TOOLTIP = {
      valueDecimals: 2,
      valueSuffix: '%',
      xDateFormat: '%A, %b %e, %Y',
    }

    export const dataLabelsFormatter = function (this: {
      x: number
      y: number
      point: { options: { label: string } }
    }) {
      return this.point.options.label !== ''
        ? `<span>${this.y.toFixed(2)}</span>` + '%'
        : undefined
    }

    const getMarginChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesWithTwoMeansChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: true,
        }),
        id: `${config.chart}-${idx}`,
        name: idx === 0 ? 'Operating margin' : 'Profit margin',
        type: 'line',
        yAxis: 'default',
      }))

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: omit(['selected'], range6m1y5y20yAll),
          series: [
            ...series,
            ...getMeansSeries({
              series: data.data[0],
              meanPoints: [data.mean_data[0], data.mean_data[1]],
              id: 'Operating margin',
              color: Palette.darkBlue,
            }),
            ...getMeansSeries({
              series: data.data[1],
              meanPoints: [data.mean_data[2], data.mean_data[3]],
              id: 'Profit margin',
              color: Palette.darkGreen,
            }),
          ],
          tooltip: PERCENTUAL_TOOLTIP,
          xAxis: datetimeXAxis,
          yAxis: PERCENTUAL_Y_AXIS,
        },
        stockTools: true,
      }
    }

    const getFreeCashFlowChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesWithTwoMeansChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        id: `${config.chart}-${idx}`,
        type: 'line',
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          100 * value,
        ]),
        name:
          config.chart === 'free_cash_flow_per_earnings'
            ? 'FCF to earnings'
            : config.chart === 'free_cash_flow_per_sales'
            ? 'FCF to sales'
            : config.chart === 'payout'
            ? 'Payout'
            : '',
        yAxis: 'default',
      }))

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: omit(['selected'], range6m1y5y20yAll),
          series: [
            ...series,
            ...getMeansSeries({
              series: data.data[0],
              meanPoints: data.mean_data,
              id: 'default',
              color: Palette.darkBlue,
            }),
          ],
          tooltip: PERCENTUAL_TOOLTIP,
          xAxis: datetimeXAxis,
          yAxis: PERCENTUAL_Y_AXIS,
        },
        stockTools: true,
      }
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          addMinMaxIndicatorsIndexes: [0],
          chartTitle: config.chart === 'roe' ? 'ROE' : 'ROA',
          convertToPercentages: [true, true],
          msdIndicators: { avg: true, sd1: true, sd2: true },
          msdIndicatorsSeriesIndexes: [0],
          rangeSelector: omit(['selected'], range6m1y5y20yAll),
        },
        {
          boostThreshold: Array(data.data.length).fill(10000),
          color: [Palette.darkBlue, Palette.darkBlue, '#FFF'],
          dashStyle: ['Solid', 'DashDot'],
          name:
            config.chart === 'roe'
              ? ['ROE', 'Previsão ROE']
              : ['ROA', 'Previsão ROA'],
          enableMouseTracking: [true, true, false],
          showInLegend: [true, true, false],
        }
      )(config, data)
  }
}
